/**
 * Dev-only order summary traces. Quiet by default; enable with:
 *   localStorage.setItem("DEBUG_ORDER_SUMMARY", "1")
 *
 * Optional `opts.changed === true` or `payload.changed === true` logs even when the
 * localStorage flag is off (callers should use sparingly).
 */

const LS_KEY = "DEBUG_ORDER_SUMMARY";

/** @type {Record<string, unknown>} */
const _lastDedupByLabel = Object.create(null);

function _stableStringify(v) {
  try {
    return JSON.stringify(v, (_, val) =>
      typeof val === "bigint" ? String(val) : val
    );
  } catch {
    return "";
  }
}

function _isDev() {
  try {
    return import.meta.env?.DEV === true;
  } catch {
    return false;
  }
}

function _localFlagOn() {
  try {
    return typeof localStorage !== "undefined" && localStorage.getItem(LS_KEY) === "1";
  } catch {
    return false;
  }
}

function _stripChangedFlag(p) {
  if (!p || typeof p !== "object" || Array.isArray(p)) return p;
  if (!Object.prototype.hasOwnProperty.call(p, "changed")) return p;
  const { changed: _ignored, ...rest } = p;
  return rest;
}

/**
 * Narrow fingerprint for [LDS_SUCCESS_TOTAL_DEBUG] — only these may trigger a new log.
 */
export function buildLdsSuccessTotalDebugDedup({
  sumpaid,
  total,
  baggagePrice,
  creditCardFee,
  priceWithCommissionAndCreditCardFee,
}) {
  return {
    sumpaid: sumpaid ?? null,
    total: total ?? null,
    baggagePrice: baggagePrice ?? null,
    creditCardFee: creditCardFee ?? null,
    priceWithCommissionAndCreditCardFee: priceWithCommissionAndCreditCardFee ?? null,
  };
}

/**
 * @param {string} label e.g. "[ORDER_SUMMARY_BAGGAGE_RAW]"
 * @param {Record<string, unknown>} payload
 * @param {{ changed?: boolean, dedup?: unknown }} [opts] `dedup` overrides value used for last-seen comparison
 */
export function safeOrderSummaryTrace(label, payload, opts = {}) {
  if (!_isDev()) return;
  const payloadChanged =
    Boolean(
      payload &&
        typeof payload === "object" &&
        !Array.isArray(payload) &&
        payload.changed === true
    );
  const allow = _localFlagOn() || opts.changed === true || payloadChanged;
  if (!allow) return;

  const dedup =
    opts.dedup !== undefined ? opts.dedup : _stripChangedFlag(payload);
  const prev = _lastDedupByLabel[label];
  if (_stableStringify(prev) === _stableStringify(dedup)) return;
  _lastDedupByLabel[label] = dedup;

  let out = payload;
  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Object.prototype.hasOwnProperty.call(payload, "changed")
  ) {
    const { changed: _ignored, ...rest } = payload;
    out = rest;
  }
  // eslint-disable-next-line no-console
  console.warn(label, out);
}
